/**
 * Piezas chicas compartidas por los bloques del P&L: control segmentado,
 * selector de mes, cabecera de sección numerada y las notas (supuestos + de
 * dónde sale cada número) que se prenden con el switch "Notas".
 */

import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { PnlFmt } from './pnl-format';

export interface SegOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export function Seg<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: SegOption<T>[];
  ariaLabel: string;
  className?: string;
}) {
  return (
    <div role="group" aria-label={ariaLabel} className={cn('inline-flex rounded-lg border border-border bg-muted p-0.5', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          disabled={o.disabled}
          onClick={() => !o.disabled && onChange(o.value)}
          className={cn(
            'rounded-md px-3 py-1 text-xs font-semibold text-muted-foreground transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-40',
            value === o.value && 'bg-card text-foreground shadow-xs'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Selector de un mes cerrado (1..último), con el nombre largo y el año. Lo usan la cabecera y las secciones con período "Mes". */
export function PnlMonthSelect({
  value,
  onChange,
  months,
  year,
  fmt,
  ariaLabel,
  className,
}: {
  value: number;
  onChange: (m: number) => void;
  months: number[];
  year: number;
  fmt: PnlFmt;
  ariaLabel: string;
  className?: string;
}) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className={cn('h-8 w-[150px] text-xs', className)} aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {months.map((m) => (
          <SelectItem key={m} value={String(m)}>
            {fmt.monthLong(m)} {year}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function SegLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">{children}</span>;
}

export function SectionHead({ num, title, sub }: { num: string; title: string; sub?: React.ReactNode }) {
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-3">
      <span className="rounded border border-border px-1.5 py-px font-mono text-[11px] font-semibold text-muted-foreground">{num}</span>
      <h2 className="text-xl font-bold tracking-tight text-balance">{title}</h2>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export type NoteTag = 'sign' | 'color' | 'complete' | 'source' | 'idea' | 'label' | 'closed' | 'location' | 'decision' | 'rule' | 'warn' | 'form' | 'new';

export interface NoteItem {
  tag: NoteTag;
  title: string;
  text: string;
}

const TAG_KEY: Record<NoteTag, string> = {
  sign: 'budget_control.notes.tag.sign',
  color: 'budget_control.notes.tag.color',
  complete: 'budget_control.notes.tag.complete',
  source: 'budget_control.notes.tag.source',
  idea: 'budget_control.notes.tag.idea',
  label: 'budget_control.notes.tag.label',
  closed: 'budget_control.notes.tag.closed',
  location: 'budget_control.notes.tag.location',
  decision: 'budget_control.notes.tag.decision',
  rule: 'budget_control.notes.tag.rule',
  warn: 'budget_control.notes.tag.warn',
  form: 'budget_control.notes.tag.form',
  new: 'budget_control.notes.tag.new',
};

export function PnlNote({ show, items }: { show: boolean; items: NoteItem[] }) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <div className="mt-2.5 rounded-r-lg border-l-[3px] border-primary bg-muted/60 px-3.5 py-3 text-xs text-muted-foreground">
      {items.map((n, i) => (
        <p key={i} className={cn(i < items.length - 1 && 'mb-2')}>
          <span
            className={cn(
              'mr-1.5 inline-block rounded-sm border px-1 align-[1px] font-mono text-[10px] font-semibold uppercase tracking-[0.06em]',
              n.tag === 'warn' ? 'border-(--pnl-warn) text-(--pnl-warn)' : 'border-primary text-primary'
            )}
          >
            {t(TAG_KEY[n.tag])}
          </span>
          <b className="font-semibold text-foreground">{n.title}</b> {n.text}
        </p>
      ))}
    </div>
  );
}
